import React, { useState, useEffect } from 'react';
import {
    ChevronRight, ChevronDown, RefreshCw, Sparkles, Play, Image as ImageIcon,
    TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Pause, Scale,
    Target, DollarSign, Eye, MousePointer, Grid, Layers
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent } from '../utils/helpers';
import {
    fetchCampaignCreatives,
    analyzeCampaign,
    analyzeCreative
} from '../services/campaignCreativesService';
import CreativeCard from './CreativeCard';
import CampaignAIAnalysis from './CampaignAIAnalysis';
import PlacementGroupedView from './PlacementGroupedView';

const CampaignCreativeRow = ({
    campaign,
    isExpanded,
    onToggle,
    dateRange,
    userId,
    brandId,
    creativeAnalysisCache,
    campaignAnalysisCache,
    onCreativeAnalysisUpdate,
    onCampaignAnalysisUpdate
}) => {
    const [creatives, setCreatives] = useState([]);
    const [loadingCreatives, setLoadingCreatives] = useState(false);
    const [creativesError, setCreativesError] = useState(null);
    const [analyzingCampaign, setAnalyzingCampaign] = useState(false);
    const [analyzingCreativeId, setAnalyzingCreativeId] = useState(null);
    const [expandedCreativeId, setExpandedCreativeId] = useState(null);
    const [viewMode, setViewMode] = useState('all'); // 'all' | 'by-placement'

    const metrics = campaign.currentMetrics || {};
    const roas = metrics.roas || 0;
    const campaignAnalysis = campaignAnalysisCache[campaign.platformCampaignId];

    // Fetch creatives when expanded
    useEffect(() => {
        if (isExpanded && creatives.length === 0 && !loadingCreatives) {
            fetchCreatives();
        }
    }, [isExpanded]);

    const fetchCreatives = async () => {
        setLoadingCreatives(true);
        setCreativesError(null);
        try {
            const result = await fetchCampaignCreatives(
                campaign.platformCampaignId,
                userId,
                brandId,
                campaign.platform,
                dateRange
            );
            setCreatives(result.creatives || []);
        } catch (error) {
            console.error('Error fetching creatives:', error);
            setCreativesError(error.message);
        } finally {
            setLoadingCreatives(false);
        }
    };

    const handleAnalyzeCampaign = async () => {
        if (creatives.length === 0) return;
        setAnalyzingCampaign(true);
        try {
            const analysis = await analyzeCampaign(
                campaign.platformCampaignId,
                userId,
                brandId,
                campaign.platform,
                creatives,
                { name: campaign.name, status: metrics.status }
            );
            onCampaignAnalysisUpdate(campaign.platformCampaignId, analysis);
        } catch (error) {
            console.error('Error analyzing campaign:', error);
        } finally {
            setAnalyzingCampaign(false);
        }
    };

    const handleAnalyzeCreative = async (creative) => {
        setAnalyzingCreativeId(creative.id);
        try {
            const analysis = await analyzeCreative(creative, userId, brandId);
            onCreativeAnalysisUpdate(creative.id, analysis);
        } catch (error) {
            console.error('Error analyzing creative:', error);
        } finally {
            setAnalyzingCreativeId(null);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'var(--color-success)';
            case 'PAUSED': return 'var(--color-warning)';
            default: return 'var(--color-text-tertiary)';
        }
    };

    const getStatusBg = (status) => {
        switch (status) {
            case 'ACTIVE': return 'var(--color-success-bg)';
            case 'PAUSED': return 'var(--color-warning-bg)';
            default: return 'var(--color-bg-tertiary)';
        }
    };

    return (
        <div style={{ borderBottom: '1px solid var(--color-border-light)' }}>
            {/* Campaign Summary Row */}
            <div
                onClick={onToggle}
                style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 1fr 80px',
                    gap: 'var(--space-4)',
                    padding: 'var(--space-4)',
                    cursor: 'pointer',
                    background: isExpanded ? 'var(--color-bg-secondary)' : 'transparent',
                    transition: 'background var(--transition-fast)'
                }}
                className="hover-bg"
            >
                {/* Expand Icon */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isExpanded ? (
                        <ChevronDown size={20} className="text-secondary" />
                    ) : (
                        <ChevronRight size={20} className="text-secondary" />
                    )}
                </div>

                {/* Campaign Name */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                    <span style={{ fontWeight: 500 }}>{campaign.name}</span>
                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {campaign.objective || 'No objective set'}
                    </span>
                </div>

                {/* Spend */}
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(metrics.spend || 0)}</span>
                </div>

                {/* Revenue */}
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(metrics.revenue || 0)}</span>
                </div>

                {/* ROAS */}
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <span style={{
                        fontWeight: 600,
                        color: roas >= 2 ? 'var(--color-success)' : roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                    }}>
                        {roas.toFixed(2)}x
                    </span>
                </div>

                {/* Status */}
                <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        background: getStatusBg(metrics.status),
                        color: getStatusColor(metrics.status)
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                        {metrics.status || 'UNKNOWN'}
                    </span>
                </div>

                {/* AI Badge */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {campaignAnalysis && (
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 600,
                            background: campaignAnalysis.campaignScore >= 7 ? 'var(--color-success-bg)' :
                                campaignAnalysis.campaignScore >= 5 ? 'var(--color-warning-bg)' : 'var(--color-error-bg)',
                            color: campaignAnalysis.campaignScore >= 7 ? 'var(--color-success)' :
                                campaignAnalysis.campaignScore >= 5 ? 'var(--color-warning)' : 'var(--color-error)'
                        }}>
                            <Sparkles size={12} />
                            {campaignAnalysis.campaignScore?.toFixed(1)}
                        </span>
                    )}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div style={{
                    padding: 'var(--space-6)',
                    background: 'var(--color-bg-secondary)',
                    borderTop: '1px solid var(--color-border-light)'
                }}>
                    {/* Loading State */}
                    {loadingCreatives && (
                        <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                            <RefreshCw size={24} className="spin text-secondary" />
                            <p className="text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                                Loading creatives...
                            </p>
                        </div>
                    )}

                    {/* Error State */}
                    {creativesError && (
                        <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                            <AlertTriangle size={32} className="text-error" style={{ opacity: 0.5 }} />
                            <p className="text-error" style={{ marginTop: 'var(--space-2)' }}>
                                {creativesError}
                            </p>
                            <button
                                onClick={fetchCreatives}
                                className="btn btn-secondary"
                                style={{ marginTop: 'var(--space-4)' }}
                            >
                                <RefreshCw size={16} /> Retry
                            </button>
                        </div>
                    )}

                    {/* Empty State */}
                    {!loadingCreatives && !creativesError && creatives.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                            <ImageIcon size={48} className="text-secondary" style={{ opacity: 0.3 }} />
                            <h4 style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                                No Creatives Found
                            </h4>
                            <p className="text-secondary">
                                This campaign doesn't have any creatives yet.
                            </p>
                        </div>
                    )}

                    {/* Creatives Content */}
                    {!loadingCreatives && !creativesError && creatives.length > 0 && (
                        <>
                            {/* Campaign AI Analysis Section */}
                            <CampaignAIAnalysis
                                campaign={campaign}
                                creatives={creatives}
                                analysis={campaignAnalysis}
                                isAnalyzing={analyzingCampaign}
                                onAnalyze={handleAnalyzeCampaign}
                            />

                            {/* Creatives Header */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: 'var(--space-6)',
                                marginBottom: 'var(--space-4)'
                            }}>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <ImageIcon size={18} />
                                    Creatives ({creatives.length})
                                </h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    {/* View Toggle */}
                                    <div style={{
                                        display: 'flex',
                                        background: 'var(--color-bg-tertiary)',
                                        borderRadius: 'var(--radius-md)',
                                        padding: '2px'
                                    }}>
                                        <button
                                            onClick={() => setViewMode('all')}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)',
                                                padding: 'var(--space-2) var(--space-3)',
                                                borderRadius: 'var(--radius-sm)',
                                                border: 'none',
                                                background: viewMode === 'all' ? 'var(--color-surface)' : 'transparent',
                                                color: viewMode === 'all' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                                                cursor: 'pointer',
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: viewMode === 'all' ? 500 : 400,
                                                boxShadow: viewMode === 'all' ? 'var(--shadow-sm)' : 'none',
                                                transition: 'all var(--transition-fast)'
                                            }}
                                        >
                                            <Grid size={14} />
                                            All
                                        </button>
                                        <button
                                            onClick={() => setViewMode('by-placement')}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)',
                                                padding: 'var(--space-2) var(--space-3)',
                                                borderRadius: 'var(--radius-sm)',
                                                border: 'none',
                                                background: viewMode === 'by-placement' ? 'var(--color-surface)' : 'transparent',
                                                color: viewMode === 'by-placement' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                                                cursor: 'pointer',
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: viewMode === 'by-placement' ? 500 : 400,
                                                boxShadow: viewMode === 'by-placement' ? 'var(--shadow-sm)' : 'none',
                                                transition: 'all var(--transition-fast)'
                                            }}
                                        >
                                            <Layers size={14} />
                                            By Placement
                                        </button>
                                    </div>
                                    <button
                                        onClick={fetchCreatives}
                                        className="btn btn-ghost"
                                        style={{ padding: 'var(--space-2)' }}
                                    >
                                        <RefreshCw size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Creatives Content */}
                            {viewMode === 'all' ? (
                                /* All Creatives Grid */
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                    gap: 'var(--space-4)'
                                }}>
                                    {creatives.map((creative, index) => {
                                        // Build unique key for matching (same as server-side)
                                        const creativeKey = `${creative.id}-${creative.adSetId || index}`;
                                        return (
                                            <CreativeCard
                                                key={creativeKey}
                                                creative={creative}
                                                analysis={creativeAnalysisCache[creative.id]}
                                                isExpanded={expandedCreativeId === creative.id}
                                                isAnalyzing={analyzingCreativeId === creative.id}
                                                onToggleExpand={() => setExpandedCreativeId(
                                                    expandedCreativeId === creative.id ? null : creative.id
                                                )}
                                                onAnalyze={() => handleAnalyzeCreative(creative)}
                                                isTopPerformer={campaignAnalysis?.topPerformer?.creativeKey === creativeKey}
                                                recommendation={campaignAnalysis?.recommendations?.find(
                                                    r => r.creativeKey === creativeKey
                                                )}
                                            />
                                        );
                                    })}
                                </div>
                            ) : (
                                /* Grouped by Placement View */
                                <PlacementGroupedView
                                    creatives={creatives}
                                    platform={campaign.platform}
                                    creativeAnalysisCache={creativeAnalysisCache}
                                    campaignAnalysis={campaignAnalysis}
                                    expandedCreativeId={expandedCreativeId}
                                    analyzingCreativeId={analyzingCreativeId}
                                    onToggleCreativeExpand={(id) => setExpandedCreativeId(
                                        expandedCreativeId === id ? null : id
                                    )}
                                    onAnalyzeCreative={handleAnalyzeCreative}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CampaignCreativeRow;
